import { useState } from "react";
import { ProviderWithProducts } from "../../../../hooks/useProviders";
import { useApp } from "../../../../contexts/AppContext";

const DEFAULT_FAQS = [
  { question: "購入後、どのくらいの期間視聴できますか？", answer: "購入後は無期限でご視聴いただけます。繰り返し学習にご活用ください。" },
  { question: "スマートフォンでも視聴できますか？", answer: "はい。スマートフォン・タブレット・パソコンのすべてに対応しています。" },
  { question: "購入後に講師に質問できますか？", answer: "はい。購入後はメッセージ機能で講師に直接ご質問いただけます。" },
  { question: "返金・キャンセルはできますか？", answer: "デジタルコンテンツの性質上、購入後の返金は原則承っておりません。事前に無料サンプル動画をご確認ください。" },
];

interface Props {
  provider: ProviderWithProducts;
}

export default function ProviderFaqSection({ provider }: Props) {
  const { openReport } = useApp();
  const providerFaqs = (provider.faq || []).map((f) => ({
    question: f.question,
    answer: f.answer,
  }));
  const faqs = [...providerFaqs, ...DEFAULT_FAQS];
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section id="faq" className="py-14 px-4 md:px-0 border-t border-stone-100">
      <div className="mb-8">
        <p className="text-gold text-xs font-display italic tracking-widest mb-1">FAQ</p>
        <h2 className="font-serif text-deep-green text-2xl font-light">よくある質問</h2>
      </div>
      <div className="max-w-3xl flex flex-col divide-y divide-stone-100">
        {faqs.map((faq, i) => (
          <div key={i} className="py-5">
            <button
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-start justify-between gap-4 text-left cursor-pointer group"
            >
              <div className="flex items-start gap-3 flex-1">
                <span className="font-display text-gold font-light text-lg leading-none mt-0.5">Q.</span>
                <p className={`font-sans text-sm md:text-base leading-snug transition-colors ${open === i ? "text-teal" : "text-charcoal group-hover:text-deep-green"}`}>
                  {faq.question}
                </p>
              </div>
              <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-all duration-300 ${open === i ? "bg-teal/10 rotate-45" : "bg-stone-100"}`}>
                <i className={`ri-add-line text-sm ${open === i ? "text-teal" : "text-charcoal/40"}`} />
              </div>
            </button>
            {open === i && (
              <div className="flex gap-3 mt-4">
                <span className="font-display text-charcoal/30 font-light text-lg leading-none mt-0.5">A.</span>
                <p className="text-sm text-charcoal/65 font-sans leading-relaxed">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Report section */}
      <div className="mt-10 p-5 bg-stone-50 rounded-xl border border-stone-100 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-sans text-charcoal font-medium mb-1">この事業者への通報</p>
          <p className="text-xs text-charcoal/50 font-sans">不適切な出品・内容がある場合はご報告ください。</p>
        </div>
        <button
          onClick={() => openReport({ type: "provider", id: provider.id, title: provider.service_name ?? provider.name })}
          className="flex items-center gap-2 text-xs font-sans text-charcoal/40 hover:text-red-500 transition-colors cursor-pointer border border-stone-200 px-4 py-2 rounded-full hover:border-red-300 whitespace-nowrap"
        >
          <i className="ri-flag-line" />
          通報する
        </button>
      </div>
    </section>
  );
}
